import ac from 'argument-contracts';
import { AppletBase } from '@meltwater/applet-base';
import {
    installAppletHostBackwardsCompatibilityV0,
    installAppletHostBackwardsCompatibilityV1,
    VERSION_ONE_APPLET_HOST_NAMESPACE
} from '../install-applet-host-backwards-compatibility';
import { AppletHostSettingsChangeEventRegistrar } from '../applet-host-settings-change-event-registrar';
import {
    default as coerce,
    coerceArray
} from '@meltwater/coerce';
import { DateRange } from '@meltwater/date-range';
import { FormattedDateTime } from '../formatted-date-time';
import { getRuntimeReference } from '../applet-host-self-factory';
import { Filter } from '@meltwater/filters';
import { InputId } from '@meltwater/inputs';
import { logger } from '../logger';
import { AppletHostError } from '../applet-host-error';
import { AppletHostErrorCode } from '../applet-host-error-code';
import { isValidRegistrar } from '../is-valid-registrar';
import { Namespace } from '../namespace';
import { DEFAULT_NAMESPACE } from '../default-namespace';
import { version } from '../../package.json';

// Imports for typings
/* eslint-disable no-unused-vars */
import { DateRangeRepository } from '../date-range-repository';
import { FiltersRepository } from '../filters-repository';
import { InputsRepository } from '../inputs-repository';
/**
 * @typedef InputId
 */
/**
 * @typedef AppletBase
 */
/**
 * @typedef Registrar
 */
/* eslint-enable no-unused-vars */

const DEBOUNCE_DELAY = 250;
const FallbackSetterProperties = {
    dateRangeRepository: 'dateRangeRepository',
    filtersRepository: 'filtersRepository',
    inputsRepository: 'inputsRepository',
    secondaryDateRangeRepository: 'secondaryDateRangeRepository'
};

/**
 * A namespace that will have all repositories and providers fallback to the DEFAULT_NAMESPACE
 */
export const LABEL_ONLY_NAMESPACE = {};

async function throwIfAppletHostInstalled(globalThisNamespace, self) {
    if (globalThis[globalThisNamespace]) {
        const PreviousAppletHost = globalThis[globalThisNamespace].AppletHost;
        const previousAppletHost = await PreviousAppletHost.getInstance();

        if (previousAppletHost && !self.__instance) {
            throw new AppletHostError({
                message: `The AppletHost for v${version} has not been installed yet, but the AppletHost for a previous version has already been installed. Please call install from AppletHost v${version}.`,
                code: AppletHostErrorCode.CURRENT_VERSION_HOST_NOT_INSTALLED
            });
        }
    }
}

/**
 * @async
 * @callback coordinatedQueryTimeProvider
 * @param {object} options - SEE BELOW
 * @param {string} options.namespace - The namespace to get the date range from
 * @returns {Promise<FormattedDateTime>} - A FormattedDateTime to coordinate queries
 */

/**
 * @typedef {Object} NamespaceOptions
 *
 * If a repository or provider is not provided, then it will fall back to using the default namespaces version
 *
 * @property {DateRangeRepository} [dateRangeRepository] - A repository for getting/setting the current date range
 * @property {DateRangeRepository} [secondaryDateRangeRepository] - A repository for getting/setting a secondary date range (Eg. Comparison Period)
 * @property {InputsRepository} [inputsRepository] - A repository for getting/setting the current array of inputs
 * @property {coordinatedQueryTimeProvider} [coordinatedQueryTimeProvider] - A function that returns a promise of a FormattedDateTime object to synchronize all queries with
 */

/**
 * The hosting class for all applets. This class is a singleton and should be instantiated using AppletHost.install(). See method for more information.
 * @property {AppletHostSettingsChangeEventRegistrar} settingsChangeEventRegistrar - The registrar for receiving callback events when the AppletHost's settings change
 *
 * @param {object} options - **DO NOT CALL CONSTRUCTOR DIRECTLY**. Please call AppletHost.install() to instantiate.
 */
export class AppletHost {

    /**
     * Get a promise of the instance of the AppletHost.  The promise will be resolved when AppletHost.install is invoked successfully.
     * @returns {Promise<AppletHost>} A promise that will resolve to an AppletHost
     */
    static async getInstance() {
        const self = getRuntimeReference();
        await throwIfAppletHostInstalled(VERSION_ONE_APPLET_HOST_NAMESPACE, self);
        return self.__createInstancePromise();
    }

    static __createInstancePromise() {
        const self = getRuntimeReference();
        if(!self.__instancePromise) {
            self.__instancePromise = new Promise(resolve => {
                self.__instancePromiseResolve = (value) => {
                    self.__instance = value;
                    resolve(value);
                };
            });
        }
        return self.__instancePromise;
    }

    /**
     * Instantiate the applet host. Once called AppletHost.getInstance() will resolve with an instance of the AppletHost.
     * @param {object} options - SEE BELOW
     * @param {DateRangeRepository} options.dateRangeRepository - A repository for getting/setting the current date range
     * @param {DateRangeRepository} [options.secondaryDateRangeRepository] - A repository for getting/setting a secondary date range (Eg. Comparison Period)
     * @param {FiltersRepository} [options.filtersRepository] - A repository for getting/setting the current array of filters. If none is provided, `getFilters` will return an empty array.
     * @param {InputsRepository} options.inputsRepository - A repository for getting/setting the current array of inputs
     * @param {coordinatedQueryTimeProvider} options.coordinatedQueryTimeProvider - A function that returns a promise of a FormattedDateTime object to synchronize all queries with
     * @param {Object<string, NamespaceOptions>} [options.namespaces] - A set of namespaces
     * @param {Object<string, Registrar>} [options.registrars] - A key value set where the key is the string id of the registrar and the value is an instance of the Registrar
     * @returns {AppletHost} The instantiated applet host. Also available via AppletHost.getInstance()
     * @throws {AppletHostError} If the AppletHost has already been installed
     */
    static install({
        dateRangeRepository,
        secondaryDateRangeRepository,
        inputsRepository,
        filtersRepository,
        coordinatedQueryTimeProvider,
        namespaces = {},
        registrars
    }) {
        const self = getRuntimeReference();
        self.__createInstancePromise();

        if(self.__instance) {
            throw new AppletHostError({
                message: 'AppletHost has already been installed. You cannot install multiple times.',
                code: AppletHostErrorCode.DUPLICATE_HOST_OF_CURRENT_VERSION
            });
        }

        const appletInstance = new AppletHost({
            dateRangeRepository,
            secondaryDateRangeRepository,
            inputsRepository,
            filtersRepository,
            coordinatedQueryTimeProvider,
            namespaces,
            registrars
        });

        if(self.__registrationQueue) {
            for(const applet of Object.values(self.__registrationQueue)) {
                appletInstance.__register(applet);
            }
            delete self.__registrationQueue;
        }

        self.__instancePromiseResolve(appletInstance);

        installAppletHostBackwardsCompatibilityV0({
            appletHostInstallOptions: {
                dateRangeRepository,
                secondaryDateRangeRepository,
                inputsRepository,
                coordinatedQueryTimeProvider,
                namespaces,
                registrars
            },
            currentAppletHost: appletInstance
        });

        installAppletHostBackwardsCompatibilityV1({
            appletHostInstallOptions: {
                dateRangeRepository,
                secondaryDateRangeRepository,
                inputsRepository,
                coordinatedQueryTimeProvider,
                namespaces,
                registrars
            },
            currentAppletHost: appletInstance
        });

        return appletInstance;
    }

    /**
     * Register a new applet
     * @param {AppletBase} applet - The applet to be registered in the shape of https://github.com/meltwater/applet-base
     * @throws {AppletHostError} `code` property is {@link AppletHostErrorCode.DUPLICATE_TAG_NAME}, applet with the same tagName has already been registered
     * @returns {undefined}
     */
    static register(applet) {
        coerce(applet, AppletBase, `Applet must be coerceable to AppletBase. Provided value: ${applet}`);
        const self = getRuntimeReference();

        if(self.__instance) {
            self.__instance.__register(applet);
        } else {
            if(!self.__registrationQueue) {
                self.__registrationQueue = [];
            }
            if(self.__registrationQueue[applet.tagName]) {
                throw new AppletHostError({
                    message: `Duplicate tagName registering applet. Provided value: ${JSON.stringify(applet)}`,
                    code: AppletHostErrorCode.DUPLICATE_TAG_NAME
                });
            }

            self.__registrationQueue[applet.tagName] = applet;
        }
    }

    constructor({
        dateRangeRepository,
        secondaryDateRangeRepository,
        filtersRepository,
        inputsRepository,
        namespaces = {},
        coordinatedQueryTimeProvider,
        registrars
    }) {
        ac.assertObject(namespaces, 'namespaces');

        let defaultNamespace;

        try {
            defaultNamespace = new Namespace({
                dateRangeRepository,
                filtersRepository,
                inputsRepository,
                secondaryDateRangeRepository,
                coordinatedQueryTimeProvider
            });
        } catch (error) {
            logger.log(`Could not create default namespace from options. ${error.message}`);
            throw error;
        }

        const namespacesWithFallbacks = {};
        Object.entries(namespaces).forEach(([namespaceName, namespaceOptions]) => {
            namespacesWithFallbacks[namespaceName] = new Namespace({
                ...defaultNamespace,
                ...namespaceOptions,
                fallbackPropertiesArray: calculateFallbackProperties({ defaultNamespace, namespaceOptions })
            });
        });

        this.__namespacesDefaultFallbackMap = calculateNamespaceDefaultFallbackMap({ defaultNamespace, namespaces: namespacesWithFallbacks });

        this.__namespaces = new Map(Object.entries(namespacesWithFallbacks));
        this.__namespaces.set(DEFAULT_NAMESPACE, defaultNamespace);

        this.__registeredApplets = [];
        this.settingsChangeEventRegistrar = new AppletHostSettingsChangeEventRegistrar({
            isValidNamespaceCallback: namespace => this.__namespaces.has(namespace)
        });

        this.__settingsChangeDebounceTimerIdsByNamespace = {};

        if (registrars) {
            if (Object.values(registrars).some(possibleRegistrar => !isValidRegistrar(possibleRegistrar))) {
                throw new Error(`Invalid registrar provided. Provided value: ${JSON.stringify(registrars, null, 2)}`);
            }
            this.__registrars = registrars;
        } else {
            this.__registrars = {};
        }
    }

    /**
     * Get the current date range
     *
     * @async
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to get the dateRange from
     * @returns {Promise<DateRange>} The current date range in the shape of https://github.com/meltwater/date-range
     * @throws {AppletHostError} If object returned from dateRangeProvider is not coercable to a https://github.com/meltwater/date-range.
     */
    async getDateRange({ namespace = DEFAULT_NAMESPACE } = {}) {
        this.__validateNamespace(namespace);

        const dateRange = await this.__namespaces.get(namespace).dateRangeRepository.get({ namespace });

        try {
            return coerce(dateRange, DateRange, `The registered dateRangeProvider did not return a valid DateRange. Provided value: ${dateRange}`);
        } catch(error) {
            throw new AppletHostError({
                message: error.message,
                code: AppletHostErrorCode.INVALID_OUTPUT_FROM_REPOSITORY
            });
        }
    }

    /**
     * Get a secondary date range
     *
     * @async
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to get the dateRange from
     * @returns {Promise<DateRange>} The secondary date range in the shape of https://github.com/meltwater/date-range
     * @throws {AppletHostError|Error} AppletHostError if secondayDateRangeRepository was not provided at install time. Error if object returned from secondaryDateRangeProvider is not coercable to a https://github.com/meltwater/date-range
     */
    async getSecondaryDateRange({ namespace = DEFAULT_NAMESPACE } = {}) {
        this.__validateNamespace(namespace);

        if (!this.__namespaces.get(namespace).secondaryDateRangeRepository) {
            throw new AppletHostError({
                message: 'A secondaryDateRangeRepository was not provided to the appletHost at install time.',
                code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
            });
        }
        const dateRange = await this.__namespaces.get(namespace).secondaryDateRangeRepository.get({ namespace });

        try {
            return coerce(dateRange, DateRange, `The registered secondaryDateRangeProvider did not return a valid DateRange. Provided value: ${dateRange}`);
        } catch(error) {
            throw new AppletHostError({
                message: error.message,
                code: AppletHostErrorCode.INVALID_OUTPUT_FROM_REPOSITORY
            });
        }
    }

    /**
     * Get the current filters
     *
     * @async
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to get the filters from
     * @returns {Promise<Array<Filter>>} The current filters for the Applet Host in the shape of https://github.com/meltwater/filters
     * @throws {AppletHostError} If object returned from FiltersRepository is not an array coercable to a https://github.com/meltwater/filters}
     */
    async getFilters({ namespace = DEFAULT_NAMESPACE } = {}) {
        this.__validateNamespace(namespace);

        const filters = this.__namespaces.get(namespace).filtersRepository
            ? await this.__namespaces.get(namespace).filtersRepository.get({ namespace })
            : [];

        try {
            return coerceArray(filters, Filter, `FiltersRepository returned a value that is not coercable to a Filter. Provided value: ${JSON.stringify(filters)}`);
        } catch(error) {
            throw new AppletHostError({
                message: error.message,
                code: AppletHostErrorCode.INVALID_OUTPUT_FROM_REPOSITORY
            });
        }
    }

    /**
     * Get the current inputs
     *
     * @async
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to get the inputs from
     * @returns {Promise<Array<InputId>>} The current inputs for the Applet Host in the shape of https://github.com/meltwater/inputs
     * @throws {AppletHostError} If object returned from InputRepository is not an array coercable to a https://github.com/meltwater/inputs}
     */
    async getInputs({ namespace = DEFAULT_NAMESPACE } = {}) {
        this.__validateNamespace(namespace);

        const inputs = await this.__namespaces.get(namespace).inputsRepository.get({ namespace });
        try {
            ac.assertArray(inputs, 'inputsProvider return value');

            return inputs.map(input => coerce(input, InputId, `InputsRepository returned a value that is not coercable to an InputId. Provided value: ${JSON.stringify(input)}`));
        } catch(error) {
            throw new AppletHostError({
                message: error.message,
                code: AppletHostErrorCode.INVALID_OUTPUT_FROM_REPOSITORY
            });
        }
    }

    /**
     * Get a FormattedDateTime to coordinate queries
     *
     * @async
     * @param {object} [options] - SEE BELOW
     * @param {string} [options.namespace] - The namespace to get the coordinated query time from
     * @returns {Promise<FormattedDateTime>} A FormattedDateTime to coordinate queries
     * @throws {AppletHostError} If object returned from coordinatedQueryTimeProvider is not a valid FormattedDateTime
     */
    async getCoordinatedQueryTime({ namespace = DEFAULT_NAMESPACE } = {}) {
        this.__validateNamespace(namespace);

        const timestamp = await this.__namespaces.get(namespace).coordinatedQueryTimeProvider({ namespace });
        try {
            return coerce(timestamp, FormattedDateTime, `timestamp must be coercable to a FormattedDateTime. Provided value: ${JSON.stringify(timestamp)}`);
        } catch(error) {
            throw new AppletHostError({
                message: `The timestamp returned from the provided coordinatedQueryTimeProvider is not a valid FormattedDateTime. Provided value: ${timestamp}`,
                code: AppletHostErrorCode.INVALID_OUTPUT_FROM_PROVIDER
            });
        }
    }

    /**
     * Get all registered applets
     * @returns {AppletBase[]} All of the currently registered applets in the shape of https://github.com/meltwater/applet-base
     */
    getRegisteredApplets() {
        return this.__registeredApplets;
    }

    /**
     * Get a registrar by name
     * @param {string} registrarName - The name the registrar was installed with
     * @returns {Registrar|null} - If the registrar does not exist then null will be returned, otherwise the registrar will be returned.
     */
    getRegistrar(registrarName) {
        if (this.__registrars[registrarName]) {
            return this.__registrars[registrarName];
        } else {
            return null;
        }
    }

    /**
     * Register a new applet
     * @private
     * @param {AppletBase} applet - The applet to be registered in the shape of https://github.com/meltwater/applet-base
     * @returns {undefined}
     * @throws {AppletHostError} If applet with the same tagName has already been registered
     */
    __register(applet) {
        if(this.__registeredApplets.some(predecessor => predecessor.tagName === applet.tagName)) {
            throw new AppletHostError({
                message: `Duplicate tagName registering applet. Provided value: ${JSON.stringify(applet)}`,
                code: AppletHostErrorCode.DUPLICATE_TAG_NAME
            });
        }

        for (const [registrarKey, registrar] of Object.entries(this.__registrars)) {
            try {
                registrar.register(applet);
            } catch (error) {
                console.error(`An error was thrown registering an applet with ${registrarKey}. Inner error: ${JSON.stringify(error, null, 2)}`);
            }
        }

        this.__registeredApplets.push(applet);
    }

    /**
     * Set the current date range
     *
     * @param {object} options - SEE BELOW
     * @param {string} [options.namespace] - The namespace to set the dateRange on
     * @param {DateRange} options.dateRange - The date range to update the Applet Host with in the shape of https://github.com/meltwater/date-range
     * @throws {AppletHostError} `code` property is {@link AppletHostErrorCode.FEATURE_NOT_SUPPORTED}, the {@link DateRangeRepository} does not have a setter.
     * @throws {TypeError} the provided dateRange is not coercable to DateRange
     * @throws {Error} - An unknown error or an error specific an applet host implementation
     * @returns {undefined}
     */
    setDateRange({ namespace = DEFAULT_NAMESPACE, dateRange }) {
        try {
            this.__validateNamespace(namespace);

            const safeDateRange = coerce(dateRange, DateRange, `The value provided is not a valid DateRange. Provided value: ${dateRange}`);
            const currentNamespace = this.__namespaces.get(namespace);

            if (!currentNamespace.dateRangeRepository.set) {
                throw new AppletHostError({
                    message: 'The dateRangeRepository provided during AppletHost.install() does not have a setter.',
                    code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
                });
            }

            currentNamespace.dateRangeRepository.set(safeDateRange, namespace);
            this.__fireSettingsChangeEventRegistrarWithFallbackIfNeeded({
                namespace,
                fallbackProperty: FallbackSetterProperties.dateRangeRepository
            });
        } catch (error) {
            if (this.__settingsChangeDebounceTimerIdsByNamespace[namespace]) {
                clearTimeout(this.__settingsChangeDebounceTimerIdsByNamespace[namespace]);
            }
            logger.error(`settingsChangeEventRegistrar was not fired due to error: ${error.message}`);
            throw error;
        }
    }

    /**
     * Set the secondary date range
     *
     * @param {object} options - SEE BELOW
     * @param {string} [options.namespace] - The namespace to set the dateRange on
     * @param {DateRange} options.dateRange - The date range to update the Applet Host with in the shape of https://github.com/meltwater/date-range
     * @throws {AppletHostError} `code` property is {@link AppletHostErrorCode.FEATURE_NOT_SUPPORTED}, the secondary {@link DateRangeRepository} does not exist or does not have a setter.
     * @throws {TypeError} the provided dateRange is not coercable to DateRange
     * @throws {Error} - An unknown error or an error specific an applet host implementation
     * @returns {undefined}
     */
    setSecondaryDateRange({ namespace = DEFAULT_NAMESPACE, dateRange }) {
        this.__validateNamespace(namespace);

        const currentNamespace = this.__namespaces.get(namespace);

        if (!currentNamespace.secondaryDateRangeRepository) {
            throw new AppletHostError({
                message: 'A secondaryDateRangeRepository was not provided to the appletHost at install time.',
                code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
            });
        }
        else if (!currentNamespace.secondaryDateRangeRepository.set) {
            throw new AppletHostError({
                message: 'The secondaryDateRangeRepository provided during AppletHost.install() does not have a setter.',
                code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
            });
        }

        try {
            const safeDateRange = coerce(dateRange, DateRange, `The value provided is not a valid DateRange. Provided value: ${dateRange}`);

            currentNamespace.secondaryDateRangeRepository.set(safeDateRange, namespace);
            this.__fireSettingsChangeEventRegistrarWithFallbackIfNeeded({
                namespace,
                fallbackProperty: FallbackSetterProperties.secondaryDateRangeRepository
            });
        } catch (error) {
            if (this.__settingsChangeDebounceTimerIdsByNamespace[namespace]) {
                clearTimeout(this.__settingsChangeDebounceTimerIdsByNamespace[namespace]);
            }
            logger.error(`settingsChangeEventRegistrar was not fired due to error: ${error.message}`);
            throw error;
        }
    }

    /**
     * Set the current filters
     *
     * @param {object} options - SEE BELOW
     * @param {string} [options.namespace] - The namespace to write the new filters to
     * @param {Array<Filter>} options.filters - The filters to update the Applet Host with in the shape of https://github.com/meltwater/filters
     * @throws {AppletHostError} `code` property is {@link AppletHostErrorCode.FEATURE_NOT_SUPPORTED}, if the {@link FilterRepository} does not have a setter
     * @throws {TypeError} the provided array of filters are not coercable to FilterId
     * @throws {Error} - An unknown error or an error specific an applet host implementation
     * @returns {undefined}
     */
    setFilters({ namespace = DEFAULT_NAMESPACE, filters: filtersOption }) {
        try {
            this.__validateNamespace(namespace);

            const filters = coerceArray(filtersOption, Filter, `The value provided contains invalid Filter values. Provided value: ${JSON.stringify(filtersOption)}`);

            const currentNamespace = this.__namespaces.get(namespace);
            if (!currentNamespace.filtersRepository) {
                throw new AppletHostError({
                    message: 'A FiltersRepository was not provided to the appletHost at install time.',
                    code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
                });
            }
            else if (!currentNamespace.filtersRepository.set) {
                throw new AppletHostError({
                    message: 'The FiltersRepository provided during AppletHost.install() does not have a setter.',
                    code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
                });
            }

            currentNamespace.filtersRepository.set(filters, namespace);
            this.__fireSettingsChangeEventRegistrarWithFallbackIfNeeded({
                namespace,
                fallbackProperty: FallbackSetterProperties.filtersRepository
            });
        } catch (error) {
            if (this.__settingsChangeDebounceTimerIdsByNamespace[namespace]) {
                clearTimeout(this.__settingsChangeDebounceTimerIdsByNamespace[namespace]);
            }
            logger.error(`settingsChangeEventRegistrar was not fired due to error: ${error.message}`);
            throw error;
        }
    }

    /**
     * Set the current inputs
     *
     * @param {object} options - SEE BELOW
     * @param {string} [options.namespace] - The namespace to write the new inputs to
     * @param {Array<inputs>} options.inputs - The inputs to update the Applet Host with in the shape of https://github.com/meltwater/inputs
     * @throws {AppletHostError} `code` property is {@link AppletHostErrorCode.FEATURE_NOT_SUPPORTED}, if the {@link InputRepository} does not have a setter; {@link AppletHostErrorCode.MAX_INPUT_LIMIT_EXCEEDED}, host application doesn't support the number of inputs provided.
     * @throws {TypeError} the provided array of inputs are not coercable to InputId
     * @throws {Error} - An unknown error or an error specific an applet host implementation
     * @returns {undefined}
     */
    setInputs({ namespace = DEFAULT_NAMESPACE, inputs }) {
        try {
            this.__validateNamespace(namespace);

            ac.assertArray(inputs, 'inputs');
            const safeInputs = inputs.map(input => coerce(input, InputId, `The value provided is not a valid InputId. Provided value: ${JSON.stringify(input)}`));

            const currentNamespace = this.__namespaces.get(namespace);

            if (!currentNamespace.inputsRepository.set) {
                throw new AppletHostError({
                    message: 'The InputsRepository provided during AppletHost.install() does not have a setter.',
                    code: AppletHostErrorCode.FEATURE_NOT_SUPPORTED
                });
            }

            currentNamespace.inputsRepository.set(safeInputs, namespace);
            this.__fireSettingsChangeEventRegistrarWithFallbackIfNeeded({
                namespace,
                fallbackProperty: FallbackSetterProperties.inputsRepository
            });
        } catch (error) {
            if (this.__settingsChangeDebounceTimerIdsByNamespace[namespace]) {
                clearTimeout(this.__settingsChangeDebounceTimerIdsByNamespace[namespace]);
            }
            logger.error(`settingsChangeEventRegistrar was not fired due to error: ${error.message}`);
            throw error;
        }
    }

    __validateNamespace(namespace) {
        if (!this.__namespaces.get(namespace)) {
            throw new AppletHostError({
                code: AppletHostErrorCode.UNKNOWN_NAMESPACE,
                message: `Provided namespace does not exist on AppletHost. Provided value: ${namespace}`
            });
        }
    }

    __fireSettingsChangeEventRegistrarWithFallbackIfNeeded({ namespace, fallbackProperty }) {
        this.__fireSettingsChangeEventRegistrar({ namespace });

        if (namespace === DEFAULT_NAMESPACE && this.__namespacesDefaultFallbackMap.has(fallbackProperty)) {
            this.__namespacesDefaultFallbackMap.get(fallbackProperty).forEach(namespaceName => {
                this.__fireSettingsChangeEventRegistrar({ namespace: namespaceName });
            });
        } else if (namespace !== DEFAULT_NAMESPACE && this.__namespaces.get(namespace).fallbackPropertiesArray.includes(fallbackProperty)) {
            this.__fireSettingsChangeEventRegistrar({ namespace: DEFAULT_NAMESPACE });
        }
    }

    __fireSettingsChangeEventRegistrar({ namespace }) {
        if (this.__settingsChangeDebounceTimerIdsByNamespace[namespace]) {
            clearTimeout(this.__settingsChangeDebounceTimerIdsByNamespace[namespace]);
        }

        this.__settingsChangeDebounceTimerIdsByNamespace[namespace] = setTimeout(() => this.settingsChangeEventRegistrar.fire({ namespace }), DEBOUNCE_DELAY);
    }
}

function calculateNamespaceDefaultFallbackMap({ namespaces }) {
    const defaultFallbackMap = new Map();

    Object.entries(namespaces).forEach(([namespaceName, namespace]) => {
        namespace.fallbackPropertiesArray.forEach(property => {
            if (!defaultFallbackMap.has(property)) {
                defaultFallbackMap.set(property, []);
            }

            defaultFallbackMap.get(property).push(namespaceName);
        });
    });

    return defaultFallbackMap;
}

function calculateFallbackProperties({ defaultNamespace, namespaceOptions }) {
    const defaultPropertiesAvailable = Object.keys(defaultNamespace);
    const namespaceProperties = Object.keys(namespaceOptions);

    const fallbackProperties = [];

    defaultPropertiesAvailable.forEach(property => {
        if (!namespaceProperties.includes(property)) {
            fallbackProperties.push(property);
        }
    });

    return fallbackProperties;
}

export function __clearSingletonForTestsOnly() {
    if (typeof describe !== 'function') {
        throw new Error('This is for testing purposes only!!');
    }
    const self = getRuntimeReference();
    if (self.__instance) {
        self.__instance = null;
    }
    if (self.__instancePromise) {
        self.__instancePromise = null;
    }
    if (self.__registrationQueue) {
        self.__registrationQueue = null;
    }
}
